import { z } from 'zod';
import { ERRORS } from 'shared/constants/validation-constants';

export const EmailValidation = z
  .object({
    email: z.string().email({ message: ERRORS.invalidEmail }),
  })
  .required();

export const PasswordValidation = z
  .object({
    password: z.string().min(6, { message: ERRORS.minPassword }),
  })
  .required();

export const OldPasswordValidation = z
  .object({
    old_password: z.string().min(6, { message: ERRORS.minPassword }),
  })
  .required();

export const NewPasswordValidation = z
  .object({
    new_password: z.string().min(6, { message: ERRORS.minPassword }),
  })
  .required();

export const ConfirmPasswordValidation = PasswordValidation.extend({
  confirm_password: z.string(),
})
  .required()
  .refine((data) => data.password === data.confirm_password, {
    message: ERRORS.passwordsDoNotMatch,
    path: ['confirm_password'],
  });

export const ConfirmNewPasswordValidation = NewPasswordValidation.extend({
  confirm_new_password: z.string(),
})
  .required()
  .refine((data) => data.new_password === data.confirm_new_password, {
    message: ERRORS.passwordsDoNotMatch,
    path: ['confirm_new_password'],
  });

export const NameFormValidation = z
  .object({
    first_name: z
      .string()
      .min(2, { message: ERRORS.minFirstName })
      .max(32, { message: ERRORS.maxFirstName })
      .regex(/^[^\s].*[^\s]$/, { message: ERRORS.firstNameNotAllowedSpaces })
      .regex(/^[a-zA-Zа-яА-Я\s]*$/, { message: ERRORS.firstNameAllowedСharacters })
      .regex(/\S/, { message: ERRORS.firstNameOnlySpaces }),
    last_name: z
      .string()
      .min(2, { message: ERRORS.minLastName })
      .max(32, { message: ERRORS.maxLastName })
      .regex(/^[^\s].*[^\s]$/, { message: ERRORS.lastNameNotAllowedSpaces })
      .regex(/^[a-zA-Zа-яА-Я\s]*$/, { message: ERRORS.lastNameAllowedСharacters })
      .regex(/\S/, { message: ERRORS.lastNameOnlySpaces }),
  })
  .required();

export const PersonalInfoValidation = z.object({
  groups_info: z.string().trim().optional().or(z.literal('')),
  personal_info: z
    .string()
    .min(2, { message: ERRORS.minPersonalInfo })
    .max(100, { message: ERRORS.maxPersonalInfo })
    .regex(/\S/, { message: ERRORS.personalInfoOnlySpaces })
    .trim()
    .optional()
    .or(z.literal('')),
});

const today = new Date();

export const DateValidation = z.object({
  date: z.coerce
    .date({
      required_error: ERRORS.dateIsRequired,
      invalid_type_error: ERRORS.dateIsRequired,
    })
    .min(new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0), {
      message: ERRORS.passedDate,
    }),
});

export const TimeValidation = DateValidation.extend({
  time: z.coerce.date({
    required_error: ERRORS.timeIsRequired,
    invalid_type_error: ERRORS.timeIsRequired,
  }),
}).refine(
  (data) => {
    const userDate = Math.floor(data.date.getTime() / 1000);

    const todayExceptHours = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      0,
      0,
      0,
    );
    todayExceptHours.setHours(todayExceptHours.getHours() + 24);

    const currentTomorrow = Math.floor(todayExceptHours.getTime() / 1000);

    if (userDate >= currentTomorrow) return true;

    const userTime = Math.floor(data.time.getTime() / 1000);
    const currentTime = Math.floor(new Date().getTime() / 1000);

    if (userTime < currentTime) return false;
    return true;
  },
  {
    message: ERRORS.passedTime,
    path: ['time'],
  },
);

export const ThesisValidation = z.object({
  thesis: z
    .string()
    .min(1, { message: ERRORS.minTopic })
    .max(150, { message: ERRORS.maxTopic })
    .regex(/\S/, { message: ERRORS.topicOnlySpaces })
    .trim(),
});

export const DescriptionValidation = z.object({
  description: z
    .string()
    .min(1, { message: ERRORS.minDescription })
    .max(1000, { message: ERRORS.maxDescription })
    .regex(/\S/, { message: ERRORS.descriptionOnlySpaces })
    .trim(),
});

export const RoundValidation = z.object({
  round: z.number(),
  duration: z.number(),
});

export const PrivacyPolicyValidation = z.object({
  privacy_policy_agreed: z.boolean().refine((value) => value === true, {
    message: ERRORS.privacyPolicyRequired,
  }),
});
